import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

// El área pulsable llega a 44×44 px con un pseudoelemento centrado, no agrandando el botón:
// `sm` y los botones con alto propio por className se siguen viendo igual de pequeños, pero se
// pueden tocar (regla touch-target-minimum del DSDS). Va en la base y no en cada tamaño porque
// a `default`, `lg` e `icon`, que ya llegan solos, un pseudoelemento de 44 no les quita nada.
//
// Con clases de Tailwind y no con una utilidad propia a propósito: `src/index.css` NO se
// distribuye, así que una utilidad nuestra no llegaría al consumidor y el componente se
// instalaría sin el área ampliada — el mismo fallo de «se instala sin estilos» de la tarea 7.
const AREA_TACTIL =
	'relative before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2';

const buttonVariants = cva(
	`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium font-heading ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ${AREA_TACTIL}`,
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				// border-input (ya no border-gray-100): con gray-100 (#f3f4f6) la
				// línea era invisible sobre el fondo claro — el botón quedaba como
				// un rectángulo blanco flotando, sin contorno. --input ya está
				// corregido para llegar a 3:1 (SC 1.4.11, contorno de UI no
				// textual) — hoy = --border-strong (defecto 5, Foundations/DarkMode).
				// bg-card, no bg-background (11 sep 2026): en oscuro --background
				// (7%) y --card (12%) ya se distinguen — la maqueta de referencia
				// pinta estos botones con el fondo de card (#1C211F), no el de
				// página; en claro son casi idénticos (98%/100%), sin cambio
				// visible ahí. Ya no hace falta el parche
				// "button.border { background-color:
				// var(--card) }" del CSS global de Storybook — se retiró, resuelto en
				// el propio componente para los dos temas.
				outline: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
				// secondary = el botón con línea, no un relleno propio. Antes era
				// bg-secondary (verde claro, 146 88% 42%): se quita porque competía
				// visualmente con el primario verde en la misma pantalla — dos
				// rellenos verdes de peso parecido no dejaban claro cuál era la
				// acción principal. El secundario pasa a ser el contorneado, que es
				// la jerarquía habitual (relleno = primaria, línea = secundaria).
				secondary: 'border border-input bg-card hover:bg-accent hover:text-accent-foreground',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				// `--primary-text`, no `--primary`: un enlace es texto, y `--primary` sobre la
				// tarjeta clara da 3,14:1, por debajo de AA. Con `--primary-text`, 7,35:1; en
				// oscuro no cambia nada porque allí vale lo mismo que `--primary`.
				link: 'text-primary-text underline-offset-4 hover:underline'
			},
			size: {
				default: 'h-11 px-4 py-2',
				sm: 'h-9 px-3',
				lg: 'h-11 px-8',
				icon: 'h-11 w-11'
			},
			// Forma de píldora: es lo que chirigota consigue hoy añadiendo la clase
			// `btn-arca-primary` de su CSS a un Button (redondeo completo y algo más de
			// aire a los lados). Como prop, para que al integrar se cambie la clase por
			// el componente y no por otra clase global.
			shape: {
				default: '',
				pill: 'rounded-full'
			}
		},
		// El aire de más solo cuando hay texto: un botón de icono es cuadrado (h-10 w-10) y
		// con px-6 el icono se saldría.
		compoundVariants: [{ shape: 'pill', size: ['default', 'sm', 'lg'], className: 'px-6' }],
		defaultVariants: {
			variant: 'default',
			size: 'default',
			shape: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	/**
	 * Mientras dura una acción: muestra un indicador y deshabilita el botón.
	 *
	 * 🔴 Está aquí y no en cada producto porque es el patrón más repetido que hay. En chirigota
	 * había 53 ficheros pintando un spinner dentro de un botón a mano, cada uno con su criterio:
	 * unos deshabilitaban y otros no, unos cambiaban el texto y otros lo dejaban. Un botón que
	 * no se deshabilita mientras carga acepta el segundo clic, y eso en un formulario de
	 * aportación es una operación duplicada.
	 *
	 * Deshabilitar va incluido a propósito: si se dejara al consumidor, se olvidaría — que es
	 * justo lo que pasaba.
	 */
	loading?: boolean;
	/** Qué se anuncia mientras carga. Por defecto, «Cargando». */
	loadingLabel?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			shape,
			asChild = false,
			loading = false,
			loadingLabel = 'Cargando',
			children,
			disabled,
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : 'button';

		// Con `asChild` el hijo es quien manda: meterle un spinner dentro rompería el Slot de
		// Radix, que espera un único hijo. Se respeta el contrato y se deja el estado al
		// consumidor, pero `aria-busy` y `disabled` sí se propagan.
		const contenido =
			loading && !asChild ? (
				<>
					<Loader2 className="animate-spin" aria-hidden="true" />
					{/* El texto se queda: un botón que cambia de ancho al pulsarlo da la sensación
					    de que algo ha fallado, y además mueve lo que tenga al lado. */}
					{children}
					{/* Para quien no ve el spinner. `aria-busy` por sí solo no lo anuncian todos
					    los lectores, así que se dice con palabras, fuera de la vista. */}
					<span className="sr-only">{loadingLabel}</span>
				</>
			) : (
				children
			);

		return (
			<Comp
				className={cn(buttonVariants({ variant, size, shape, className }))}
				ref={ref}
				// `disabled` explícito gana: un consumidor puede querer el botón inhabilitado por
				// otro motivo mientras carga, pero nunca al revés.
				disabled={disabled ?? (loading && !asChild)}
				aria-busy={loading || undefined}
				{...props}
			>
				{contenido}
			</Comp>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
